import { forwardRef } from "react";
import PropTypes from "prop-types";
import { useTheme, styled } from "@mui/material/styles";
import Autocomplete from "@mui/material/Autocomplete";

const CustomAutocompleteRoot = styled(Autocomplete)(({ theme, ownerState }) => {
  const { palette = {}, borders = {} } = theme;
  const {
    bgColor,
    color,
    borderRadius,
    borderColor,
    hoverEffect,
    fullWidth = true,
  } = ownerState;

  // Background value
  const backgroundValue = bgColor ? palette[bgColor]?.main || bgColor : undefined;

  // Color value
  const colorValue = color ? palette[color]?.main || color : undefined;

  // Border radius value
  const borderRadiusValue = borders[borderRadius] || borderRadius;

  // Hover effect
  const hoverStyles = hoverEffect
    ? {
        transition: "all 0.3s ease",
        "&:hover .MuiOutlinedInput-root": {
          boxShadow: theme.shadows[2],
        },
      }
    : {};

  return {
    width: fullWidth ? "100%" : undefined,
    ...hoverStyles,
    "& .MuiOutlinedInput-root": {
      backgroundColor: backgroundValue,
      color: colorValue,
      borderRadius: borderRadiusValue,
      "& fieldset": {
        borderColor: borderColor ? palette[borderColor]?.main || borderColor : undefined,
      },
      "&:hover fieldset": {
        borderColor: borderColor ? palette[borderColor]?.main || borderColor : undefined,
      },
      "&.Mui-focused fieldset": {
        borderColor: borderColor ? palette[borderColor]?.main || borderColor : undefined,
      },
    },
    "& .MuiAutocomplete-popupIndicator": {
      color: colorValue,
    },
    "& .MuiAutocomplete-clearIndicator": {
      color: colorValue,
    },
  };
});

const CustomAutocomplete = forwardRef((props, ref) => {
  const {
    bgColor,
    color = "text.primary",
    borderRadius = "medium",
    borderColor,
    hoverEffect = false,
    fullWidth = true,
    sx,
    ...rest
  } = props;

  const theme = useTheme();

  return (
    <CustomAutocompleteRoot
      ref={ref}
      ownerState={{
        bgColor,
        color,
        borderRadius,
        borderColor,
        hoverEffect,
        fullWidth,
      }}
      fullWidth={fullWidth}
      sx={sx}
      {...rest}
    />
  );
});

CustomAutocomplete.propTypes = {
  bgColor: PropTypes.string,
  color: PropTypes.string,
  borderRadius: PropTypes.string,
  borderColor: PropTypes.string,
  hoverEffect: PropTypes.bool,
  fullWidth: PropTypes.bool,
  sx: PropTypes.object,
};

export default CustomAutocomplete;